import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { adminHeaders } from '../../lib/adminAuth';
import { tx } from '../../lib/adminI18n';
import './SummaryPage.css';
import SummaryYear from './SummaryYear';

// 管理画面サマリー（月次ビュー）  /admin/summary   設計: .plan/admin-statistics-impl-v1.1.md §9
//   集計は予約が確定したデータだけ。金額は扱わない。計算はすべてサーバー側。

const CHANNELS = ['AIRBNB', 'BOOKING_COM', 'DIRECT', 'MANUAL', 'OTHER', 'UNSET'] as const;
type Num = number | null;
interface PropRow {
  property_id: string; name: string; abbreviation: string | null; reservation_count: number; nights: number;
  guests: number; occupied_days: number; available_days: number; occupancy_rate: Num; avg_nights: Num;
  vacant_days: number; vacant_days_remaining: number; cancel_count: number; cancel_rate: Num;
  coupon_usage_count: number; cleaning_done: number; cleaning_planned: number; cleaning_avg_minutes: Num;
}
interface ChRow {
  channel: string; reservation_count: number; nights: number; avg_nights: Num; lead_time_days: Num;
  cancel_count: number; cancel_rate: Num; coupon_usage_count: number;
}
interface Data {
  period: { ym: string; from: string; to: string; days: number; today: string; tense: 'past' | 'current' | 'future' };
  summary: {
    reservation_count: number; nights: number; guests: number; guest_nights: number; occupied_days: number;
    available_days: number; occupancy_rate: Num; avg_nights: Num; lead_time_days: Num; lead_time_samples: number;
    vacant_days: number; vacant_days_remaining: number; cancel_count: number; cancel_rate: Num;
    coupon_usage_count: number; cleaning_done: number; cleaning_planned: number; cleaning_avg_minutes: Num;
  };
  by_property: PropRow[];
  by_channel: ChRow[];
  by_source?: { source: string; reservation_count: number; sales: number; avg_nights: Num }[];
  by_coupon: { code: string; type: string; usage_count: number }[];
  by_cleaner: { cleaner_id: number | null; name: string | null; done_count: number; planned_count: number; avg_minutes: Num }[];
  overlaps: { property_id: string; date: string; reservation_ids: string[]; channels: string[] }[];
  data_quality: { channel_unset: number; lead_time_anomaly: number; overlap_days: number; cleaning_time_invalid: number; ota_info_incomplete: number };
}

function thisMonthJst(): string {
  return new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 7);
}
function shiftYm(ym: string, d: number): string {
  const [y, m] = ym.split('-').map(Number);
  const t = y * 12 + (m - 1) + d;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}
const dash = (v: Num, unit = '') => (v == null ? '—' : `${v}${unit}`);
const pct = (v: Num) => (v == null ? '—' : `${v.toFixed(1)}%`);

function Bar({ v }: { v: Num }) {
  const w = v == null ? 0 : Math.max(0, Math.min(100, v));
  return <span className="sum-bar" aria-hidden="true"><span style={{ width: `${w}%` }} /></span>;
}

export default function SummaryPage() {
  const [params, setParams] = useSearchParams();
  const [ym, setYm] = useState(params.get('ym') || thisMonthJst());
  const [pid, setPid] = useState(params.get('property_id') || 'ALL');
  const [ch, setCh] = useState(params.get('channel') || 'ALL');
  const [props, setProps] = useState<{ id: string; name: string }[]>([]);
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showOverlaps, setShowOverlaps] = useState(false);
  const view: 'month' | 'year' = params.get('view') === 'year' ? 'year' : 'month';
  const thisYear = Number(thisMonthJst().slice(0, 4));
  const setView = (v: 'month' | 'year') => {
    const next = new URLSearchParams(params);
    if (v === 'year') { next.set('view', 'year'); } else { next.delete('view'); }
    setParams(next, { replace: true });
  };
  const onYearChange = useCallback((q: { year: number; basis: 'calendar' | 'fiscal'; pid: string }) => {
    setParams(new URLSearchParams({ view: 'year', year: String(q.year), basis: q.basis, property_id: q.pid }), { replace: true });
  }, [setParams]);

  useEffect(() => {
    fetch('/api/properties').then((r) => (r.ok ? r.json() : [])).then((a) => setProps((a || []).map((p: any) => ({ id: p.id, name: p.name })))).catch(() => {});
  }, []);

  const run = useCallback(async (q: { ym: string; pid: string; ch: string }) => {
    setLoading(true); setErr(null);
    const next = new URLSearchParams({ ym: q.ym, property_id: q.pid, channel: q.ch });
    setParams(next, { replace: true });
    try {
      const res = await fetch(`/api/admin/summary?${next.toString()}`, { headers: adminHeaders(), credentials: 'include' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setErr(d.error || tx('sum.loadErr')); setData(null); return; }
      setData(d as Data);
    } catch { setErr(tx('sum.loadErr')); }
    finally { setLoading(false); }
  }, [setParams]);

  useEffect(() => { if (view === 'month') run({ ym, pid, ch }); /* 初期表示・月次に戻ったとき */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const go = (d: number) => { const n = shiftYm(ym, d); setYm(n); run({ ym: n, pid, ch }); };
  const thisMonth = () => { const n = thisMonthJst(); setYm(n); run({ ym: n, pid, ch }); };

  const s = data?.summary;
  const tense = data?.period.tense;
  const vacantLabel = tense === 'current' ? tx('sum.vacantRemaining') : tense === 'past' ? tx('sum.vacantPast') : tx('sum.vacantFuture');
  const vacantValue = (r: { vacant_days: number; vacant_days_remaining: number }) => (tense === 'current' ? r.vacant_days_remaining : r.vacant_days);
  const chName = (c: string) => tx(`sum.ch.${c}` as any);
  const propName = useMemo(() => new Map((data?.by_property || []).map((p) => [p.property_id, p.abbreviation || p.name])), [data]);

  const totals = useMemo(() => {
    const rows = data?.by_property || [];
    const t = rows.reduce((a, r) => ({
      cnt: a.cnt + r.reservation_count, nights: a.nights + r.nights, occ: a.occ + r.occupied_days, avail: a.avail + r.available_days,
      vac: a.vac + vacantValue(r), cancel: a.cancel + r.cancel_count, coupon: a.coupon + r.coupon_usage_count,
      done: a.done + r.cleaning_done, planned: a.planned + r.cleaning_planned,
    }), { cnt: 0, nights: 0, occ: 0, avail: 0, vac: 0, cancel: 0, coupon: 0, done: 0, planned: 0 });
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const rsvLink = (extra: string) => `/admin/reservations?${extra}`;

  return (
    <div className="summary-page">
      <div className="sum-head">
        <h1>{tx('sum.title')}</h1>
        <div className="sum-seg sum-view" role="tablist">
          <button type="button" role="tab" aria-selected={view === 'month'} className={view === 'month' ? 'active' : ''} onClick={() => setView('month')}>{tx('sumY.viewMonth')}</button>
          <button type="button" role="tab" aria-selected={view === 'year'} className={view === 'year' ? 'active' : ''} onClick={() => setView('year')}>{tx('sumY.viewYear')}</button>
        </div>
      </div>

      {view === 'year' ? (
        <SummaryYear
          year={Number(params.get('year')) || thisYear}
          basis={params.get('basis') === 'fiscal' ? 'fiscal' : 'calendar'}
          pid={params.get('property_id') || 'ALL'}
          props={props}
          onChange={onYearChange}
        />
      ) : (<>

      {/* 検索条件 */}
      <div className="sum-filters">
        <div className="sum-month">
          <button type="button" className="sum-nav" onClick={() => go(-1)} aria-label={tx('sum.prev')} disabled={loading}>‹</button>
          <input type="month" className="sum-input" value={ym} onChange={(e) => e.target.value && setYm(e.target.value)} />
          <button type="button" className="sum-nav" onClick={() => go(1)} aria-label={tx('sum.next')} disabled={loading}>›</button>
          <button type="button" className="sum-btn-ghost" onClick={thisMonth} disabled={loading}>{tx('sum.thisMonth')}</button>
        </div>
        <label className="sum-field">
          <span>{tx('sum.property')}</span>
          <select className="sum-input" value={pid} onChange={(e) => setPid(e.target.value)}>
            <option value="ALL">{tx('sum.allProps')}</option>
            {props.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label className="sum-field">
          <span>{tx('sum.channel')}</span>
          <select className="sum-input" value={ch} onChange={(e) => setCh(e.target.value)}>
            <option value="ALL">{tx('sum.allChannels')}</option>
            {CHANNELS.map((c) => <option key={c} value={c}>{chName(c)}</option>)}
          </select>
        </label>
        <button type="button" className="sum-btn" onClick={() => run({ ym, pid, ch })} disabled={loading}>
          {loading ? tx('sum.loading') : tx('sum.run')}
        </button>
      </div>
      {data && (
        <p className="sum-period">
          {tx('sum.period', { from: data.period.from, to: data.period.to })}
        </p>
      )}

      {err && <div className="sum-error" role="alert">{err}</div>}

      {s && data && (<>
        {/* カード */}
        <div className="sum-cards">
          <div className="sum-card"><div className="sum-card-label">{tx('sum.reservations')}</div><div className="sum-card-value">{s.reservation_count}<small>{tx('sum.unitCount')}</small></div><div className="sum-card-note">{tx('sum.noteCheckin')}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.nights')}</div><div className="sum-card-value">{s.nights}<small>{tx('sum.unitNight')}</small></div><div className="sum-card-note">{tx('sum.noteStay')}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.occupancy')}</div><div className="sum-card-value">{pct(s.occupancy_rate)}</div><div className="sum-card-note">{tx('sum.noteOcc', { a: s.occupied_days, b: s.available_days })}</div></div>
          <div className="sum-card"><div className="sum-card-label">{vacantLabel}</div><div className="sum-card-value">{tense === 'current' ? s.vacant_days_remaining : s.vacant_days}<small>{tx('sum.unitDay')}</small></div>
            <div className="sum-card-note">{tense === 'current' ? tx('sum.noteVacantCur', { n: s.vacant_days }) : tx('sum.noteVacant')}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.avgNights')}</div><div className="sum-card-value">{dash(s.avg_nights)}<small>{s.avg_nights != null ? tx('sum.unitNight') : ''}</small></div><div className="sum-card-note">{tx('sum.noteCheckin')}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.leadTime')}</div><div className="sum-card-value">{dash(s.lead_time_days)}<small>{s.lead_time_days != null ? tx('sum.unitDay') : ''}</small></div><div className="sum-card-note">{tx('sum.noteLead', { n: s.lead_time_samples })}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.guests')}</div><div className="sum-card-value">{s.guests}<small>{tx('sum.unitPerson')}</small></div><div className="sum-card-note">{tx('sum.noteGuests', { n: s.guest_nights })}</div></div>
          <div className="sum-card"><div className="sum-card-label">{tx('sum.cancelRate')}</div><div className="sum-card-value">{pct(s.cancel_rate)}</div><div className="sum-card-note">{tx('sum.noteCancel', { n: s.cancel_count })}</div></div>
        </div>

        {/* 注意（予約の重複） */}
        {data.overlaps.length > 0 && (
          <section className="sum-alerts">
            <div className="sum-alert sum-alert-danger">
              <button type="button" className="sum-link" onClick={() => setShowOverlaps((v) => !v)}>
                ⚠ {tx('sum.overlap', { n: data.overlaps.length })}
              </button>
              {showOverlaps && (
                <ul className="sum-overlap-list">
                  {data.overlaps.map((o) => (
                    <li key={`${o.property_id}-${o.date}`}>
                      {o.date} {propName.get(o.property_id) || o.property_id}：{o.channels.map(chName).join(' / ')}（{o.reservation_ids.length}{tx('sum.unitCount')}）
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        {/* 施設別 */}
        {pid === 'ALL' && (
          <section className="sum-section">
            <h2>{tx('sum.byProperty')}</h2>
            <div className="sum-table-wrap">
              <table className="sum-table">
                <thead><tr>
                  <th>{tx('sum.property')}</th><th>{tx('sum.reservations')}</th><th>{tx('sum.nights')}</th><th>{tx('sum.available')}</th>
                  <th>{tx('sum.occupancy')}</th><th>{tx('sum.avgNights')}</th><th>{vacantLabel}</th><th>{tx('sum.cancel')}</th>
                  <th>{tx('sum.coupon')}</th><th>{tx('sum.cleaning')}</th>
                </tr></thead>
                <tbody>
                  {data.by_property.map((r) => (
                    <tr key={r.property_id}>
                      <td>{r.abbreviation || r.name}</td><td>{r.reservation_count}</td><td>{r.nights}</td><td>{r.available_days}</td>
                      <td className="sum-occ"><Bar v={r.occupancy_rate} />{pct(r.occupancy_rate)}</td>
                      <td>{dash(r.avg_nights)}</td><td>{vacantValue(r)}</td>
                      <td>{r.cancel_count}<small>（{pct(r.cancel_rate)}）</small></td>
                      <td>{r.coupon_usage_count}</td>
                      <td>{r.cleaning_done} / {r.cleaning_planned}{r.cleaning_avg_minutes != null && <small>（{tx('sum.avgMin', { n: r.cleaning_avg_minutes })}）</small>}</td>
                    </tr>
                  ))}
                  <tr className="sum-total">
                    <td>{tx('sum.total')}</td><td>{totals.cnt}</td><td>{totals.nights}</td><td>{totals.avail}</td>
                    <td className="sum-occ"><Bar v={s.occupancy_rate} />{pct(s.occupancy_rate)}</td>
                    <td>{dash(s.avg_nights)}</td><td>{totals.vac}</td>
                    <td>{totals.cancel}<small>（{pct(s.cancel_rate)}）</small></td><td>{totals.coupon}</td>
                    <td>{totals.done} / {totals.planned}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="sum-note">{tx('sum.notePropTable')}</p>
          </section>
        )}

        {/* 予約経路別 */}
        <section className="sum-section">
          <h2>{tx('sum.byChannel')}</h2>
          <div className="sum-table-wrap">
            <table className="sum-table">
              <thead><tr>
                <th>{tx('sum.channel')}</th><th>{tx('sum.reservations')}</th><th>{tx('sum.nights')}</th><th>{tx('sum.avgNights')}</th>
                <th>{tx('sum.leadTime')}</th><th>{tx('sum.cancel')}</th><th>{tx('sum.coupon')}</th>
              </tr></thead>
              <tbody>
                {data.by_channel.map((r) => (
                  <tr key={r.channel} className={r.reservation_count === 0 && r.cancel_count === 0 ? 'sum-zero' : ''}>
                    <td>{chName(r.channel)}</td><td>{r.reservation_count}</td><td>{r.nights}</td><td>{dash(r.avg_nights)}</td>
                    <td>{r.lead_time_days == null ? '—' : tx('sum.days', { n: r.lead_time_days })}</td>
                    <td>{r.cancel_count}<small>（{pct(r.cancel_rate)}）</small></td><td>{r.coupon_usage_count}</td>
                  </tr>
                ))}
                <tr className="sum-total">
                  <td>{tx('sum.total')}</td>
                  <td>{data.by_channel.reduce((a, r) => a + r.reservation_count, 0)}</td>
                  <td>{data.by_channel.reduce((a, r) => a + r.nights, 0)}</td>
                  <td>{dash(s.avg_nights)}</td>
                  <td>{s.lead_time_days == null ? '—' : tx('sum.days', { n: s.lead_time_days })}</td>
                  <td>{data.by_channel.reduce((a, r) => a + r.cancel_count, 0)}</td>
                  <td>{data.by_channel.reduce((a, r) => a + r.coupon_usage_count, 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="sum-note">{tx('sum.noteChannelTable')}</p>
        </section>

        {/* 流入元別（utm_source） */}
        <section className="sum-section">
          <h2>{tx('sum.bySource')}</h2>
          {!data.by_source || data.by_source.length === 0 ? <p className="sum-empty">{tx('sum.noSource')}</p> : (
            <div className="sum-table-wrap">
              <table className="sum-table">
                <thead><tr>
                  <th>{tx('sum.source')}</th><th>{tx('sum.reservations')}</th><th>{tx('sum.sales')}</th><th>{tx('sum.avgNights')}</th>
                </tr></thead>
                <tbody>
                  {data.by_source.map((r) => (
                    <tr key={r.source}>
                      <td>{r.source === '(none)' ? <span className="sum-muted">{tx('sum.sourceNone')}</span>
                        : r.source === 'official' ? tx('rsv.attrOfficial') : <code>{r.source}</code>}</td>
                      <td>{r.reservation_count}</td>
                      <td>¥{r.sales.toLocaleString()}</td>
                      <td>{dash(r.avg_nights)}</td>
                    </tr>
                  ))}
                  <tr className="sum-total">
                    <td>{tx('sum.total')}</td>
                    <td>{data.by_source.reduce((a, r) => a + r.reservation_count, 0)}</td>
                    <td>¥{data.by_source.reduce((a, r) => a + r.sales, 0).toLocaleString()}</td>
                    <td>{dash(s.avg_nights)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
          <p className="sum-note">{tx('sum.noteSourceTable')}</p>
        </section>

        <div className="sum-two">
          {/* クーポン別 */}
          <section className="sum-section">
            <h2>{tx('sum.byCoupon')}</h2>
            {data.by_coupon.length === 0 ? <p className="sum-empty">{tx('sum.noCoupon')}</p> : (
              <table className="sum-table">
                <thead><tr><th>{tx('sum.code')}</th><th>{tx('sum.type')}</th><th>{tx('sum.usage')}</th></tr></thead>
                <tbody>
                  {data.by_coupon.map((r) => (
                    <tr key={r.code}><td><code>{r.code}</code></td><td>{r.type === 'campaign' ? tx('sum.campaign') : tx('sum.repeater')}</td><td>{r.usage_count}</td></tr>
                  ))}
                  <tr className="sum-total"><td>{tx('sum.total')}</td><td /><td>{data.by_coupon.reduce((a, r) => a + r.usage_count, 0)}</td></tr>
                </tbody>
              </table>
            )}
          </section>

          {/* 清掃者別 */}
          <section className="sum-section">
            <h2>{tx('sum.byCleaner')}</h2>
            {data.by_cleaner.length === 0 ? <p className="sum-empty">{tx('sum.noCleaning')}</p> : (
              <table className="sum-table">
                <thead><tr><th>{tx('sum.cleaner')}</th><th>{tx('sum.done')}</th><th>{tx('sum.planned')}</th><th>{tx('sum.avgTime')}</th></tr></thead>
                <tbody>
                  {data.by_cleaner.map((r) => (
                    <tr key={String(r.cleaner_id)}>
                      <td>{r.cleaner_id == null ? <span className="sum-muted">{tx('sum.unassigned')}</span> : r.name}</td>
                      <td>{r.done_count}</td><td>{r.planned_count}</td>
                      <td>{r.avg_minutes == null ? '—' : tx('sum.avgMin', { n: r.avg_minutes })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="sum-note">{tx('sum.noteCleaning')}</p>
          </section>
        </div>

        {/* データの状態 */}
        <section className="sum-section sum-quality">
          <h2>{tx('sum.quality')}</h2>
          <ul>
            <li className={data.data_quality.channel_unset ? 'warn' : ''}>{tx('sum.dqChannel', { n: data.data_quality.channel_unset })}</li>
            <li className={data.data_quality.overlap_days ? 'warn' : ''}>{tx('sum.dqOverlap', { n: data.data_quality.overlap_days })}</li>
            <li className={data.data_quality.lead_time_anomaly ? 'warn' : ''}>{tx('sum.dqLead', { n: data.data_quality.lead_time_anomaly })}</li>
            <li className={data.data_quality.cleaning_time_invalid ? 'warn' : ''}>{tx('sum.dqCleanTime', { n: data.data_quality.cleaning_time_invalid })}</li>
            <li className={data.data_quality.ota_info_incomplete ? 'warn' : ''}>
              {data.data_quality.ota_info_incomplete > 0
                ? <Link to={rsvLink('tags=info_incomplete')}>{tx('sum.dqOta', { n: data.data_quality.ota_info_incomplete })}</Link>
                : tx('sum.dqOta', { n: 0 })}
            </li>
          </ul>
          <p className="sum-note">{tx('sum.noteConfirmedOnly')}</p>
        </section>
      </>)}
      </>)}
    </div>
  );
}
